import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from 'contexts/LanguageContext';
import SystemUserManager from '../../components/admin/SystemUserManager';
import SystemOrganizationManager from '../../components/admin/SystemOrganizationManager';
import PackageManager from '../../components/admin/PackageManager';
import SubscriptionManager from '../../components/admin/SubscriptionManager';
import ModuleStatus from '../../components/admin/ModuleStatus';
import AnalyticsDashboard from '../../components/admin/AnalyticsDashboard';
import SystemDashboard from '../../components/admin/SystemDashboard';
import { StorageOptimizationDashboard } from '../../components/documents/StorageOptimizationDashboard';
import { 
  ComputerDesktopIcon, 
  ClockIcon, 
  BellIcon, 
  PlayIcon,
  BuildingOfficeIcon,
  UsersIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  RectangleStackIcon,
  BanknotesIcon,
  PresentationChartLineIcon,
  ServerStackIcon,
  CubeIcon,
  CloudArrowDownIcon,
  Bars3Icon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import CommonPageHeader from '../../components/common/CommonPageHeader';
import { Card, CardHeader, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { useDeadlineReminder } from '../../services/deadlineService';
import toast from 'react-hot-toast';

const SystemAdmin = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { runDeadlineCheck } = useDeadlineReminder();
  const [isRunningDeadlineCheck, setIsRunningDeadlineCheck] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'organizations' | 'users' | 'subscriptions' | 'billing' | 'packages' | 'modules' | 'payments' | 'analytics' | 'system' | 'storage'>('overview');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Sync activeTab with URL
  useEffect(() => {
    const path = location.pathname;
    const tabFromUrl = path.split('/system-admin/')[1] || 'overview';
    setActiveTab(tabFromUrl as any);
  }, [location.pathname]);

  const handleTabChange = (tabId: string) => {
    navigate(`/system-admin/${tabId === 'overview' ? '' : tabId}`);
    setIsMobileMenuOpen(false);
    setSearchQuery('');
  };

  const handleDeadlineCheck = async () => {
    setIsRunningDeadlineCheck(true);
    try {
      const result = await runDeadlineCheck();
      toast.success(result);
    } catch (error) {
      console.error('Error running deadline check:', error);
      toast.error('Hiba történt a határidő ellenőrzés során');
    } finally {
      setIsRunningDeadlineCheck(false);
    }
  };

  const tabGroups = [
    { id: 'overview', label: 'Áttekintés', tabs: ['overview'] as const },
    { id: 'entities', label: 'Szervezetek, felhasználók', tabs: ['organizations', 'users'] as const },
    { id: 'billing', label: 'Előfizetések & Csomagok', tabs: ['subscriptions', 'packages', 'modules', 'billing', 'payments'] as const },
    { id: 'system', label: 'Rendszer', tabs: ['analytics', 'system', 'storage'] as const }
  ];

  const tabs = [
    { id: 'overview' as const, name: 'Áttekintés', icon: ChartBarIcon, description: 'Rendszer állapot és statisztikák', color: 'blue' },
    { id: 'organizations' as const, name: 'Szervezetek', icon: BuildingOfficeIcon, description: 'Szervezetek kezelése és áttekintése', color: 'purple' },
    { id: 'users' as const, name: 'Felhasználók', icon: UsersIcon, description: 'Rendszerszintű felhasználókezelés', color: 'green' },
    { id: 'subscriptions' as const, name: 'Előfizetések', icon: CreditCardIcon, description: 'Előfizetések és licencek kezelése', color: 'indigo' },
    { id: 'packages' as const, name: 'Csomagok', icon: CubeIcon, description: 'Előfizetési csomagok és modulok kezelése', color: 'orange' },
    { id: 'modules' as const, name: 'Modulok', icon: RectangleStackIcon, description: 'Modulok állapota és konzisztencia ellenőrzés', color: 'teal' },
    { id: 'billing' as const, name: 'Számlázás', icon: CurrencyDollarIcon, description: 'Számlázás és fizetések', color: 'emerald' },
    { id: 'payments' as const, name: 'Fizetések', icon: BanknotesIcon, description: 'Fizetési módok és tranzakciók', color: 'yellow' },
    { id: 'analytics' as const, name: 'Analitika', icon: PresentationChartLineIcon, description: 'Üzleti jelentések és statisztikák', color: 'pink' },
    { id: 'system' as const, name: 'Rendszer', icon: ServerStackIcon, description: 'Rendszer beállítások és eszközök', color: 'slate' },
    { id: 'storage' as const, name: 'Storage Optimalizálás', icon: CloudArrowDownIcon, description: 'Tárolás kezelés és költség optimalizálás', color: 'cyan' }
  ];

  const colorMap: Record<string, { active: string; bg: string; text: string }> = {
    blue:    { active: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',     bg: 'bg-blue-100 dark:bg-blue-900/20',    text: 'text-blue-600 dark:text-blue-400' },
    purple:  { active: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300', bg: 'bg-purple-100 dark:bg-purple-900/20', text: 'text-purple-600 dark:text-purple-400' },
    teal:    { active: 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300',     bg: 'bg-teal-100 dark:bg-teal-900/20',    text: 'text-teal-600 dark:text-teal-400' },
    green:   { active: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300',   bg: 'bg-green-100 dark:bg-green-900/20',  text: 'text-green-600 dark:text-green-400' },
    indigo:  { active: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300', bg: 'bg-indigo-100 dark:bg-indigo-900/20', text: 'text-indigo-600 dark:text-indigo-400' },
    orange:  { active: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300', bg: 'bg-orange-100 dark:bg-orange-900/20', text: 'text-orange-600 dark:text-orange-400' },
    emerald: { active: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-100 dark:bg-emerald-900/20', text: 'text-emerald-600 dark:text-emerald-400' },
    red:     { active: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300',       bg: 'bg-red-100 dark:bg-red-900/20',     text: 'text-red-600 dark:text-red-400' },
    cyan:    { active: 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300',     bg: 'bg-cyan-100 dark:bg-cyan-900/20',    text: 'text-cyan-600 dark:text-cyan-400' },
    yellow:  { active: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300', bg: 'bg-yellow-100 dark:bg-yellow-900/20', text: 'text-yellow-600 dark:text-yellow-400' },
    pink:    { active: 'bg-pink-50 dark:bg-pink-900/20 text-pink-700 dark:text-pink-300',     bg: 'bg-pink-100 dark:bg-pink-900/20',    text: 'text-pink-600 dark:text-pink-400' },
    slate:   { active: 'bg-slate-50 dark:bg-slate-900/20 text-slate-700 dark:text-slate-300',   bg: 'bg-slate-100 dark:bg-slate-900/20',  text: 'text-slate-600 dark:text-slate-400' },
    gray:    { active: 'bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-300',     bg: 'bg-gray-100 dark:bg-gray-700',      text: 'text-gray-600 dark:text-gray-400' }
  };

  const filteredTabs = tabs.filter(tab => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return tab.name.toLowerCase().includes(q) || tab.description.toLowerCase().includes(q);
  });

  const toggleGroup = (groupId: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  return (
    <div className="flex flex-col h-screen">
      <CommonPageHeader
        title={t('systemAdmin.title')}
        subtitle="Rendszerszintű adminisztráció és kezelés"
        icon={ComputerDesktopIcon}
        actionButton={
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        }
      />

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Main Content - Sidebar + Content */}
      <div className="flex-1 flex overflow-hidden pt-4 md:pt-0">
        {/* Sidebar - Navigation */}
        <div className={`
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 transition-transform duration-300 ease-in-out
          fixed md:relative z-50 md:z-auto
          w-64 h-full md:h-auto flex-shrink-0 
          bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto
        `}>
          <div className="p-3">
            {/* Mobil bezárás gomb */}
            <div className="md:hidden flex justify-between items-center mb-4 p-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Admin Menü
              </h3>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Kompakt színes fejléc */}
            <div className="mb-4 p-3 bg-primary-600 rounded-lg text-white">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-white/20 rounded-md">
                  <ComputerDesktopIcon className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Admin Panel</h3>
                  <p className="text-xs text-white/70">Rendszergazda</p>
                </div>
              </div>
            </div>
            {/* Search */}
            <div className="relative mb-3">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Admin menü keresése..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-colors"
              />
            </div>

            {/* Grouped Navigation */}
            <nav className="space-y-3">
              {tabGroups.map((group) => {
                const groupTabs = filteredTabs.filter(tab => (group.tabs as readonly string[]).includes(tab.id));
                if (groupTabs.length === 0) return null;
                const isCollapsed = collapsedGroups.has(group.id);

                return (
                  <div key={group.id}>
                    {/* Group Header */}
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className="w-full flex items-center justify-between px-2 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      <span className="text-left truncate min-w-0">{group.label}</span>
                      <ChevronDownIcon className={`h-3.5 w-3.5 flex-shrink-0 ml-2 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`} />
                    </button>

                    {/* Group Tabs */}
                    {!isCollapsed && (
                      <div className="space-y-0.5 mt-1">
                        {groupTabs.map((tab) => {
                          const Icon = tab.icon;
                          const isActive = activeTab === tab.id;
                          const colors = colorMap[tab.color] || colorMap.gray;

                          return (
                            <button
                              key={tab.id}
                              onClick={() => handleTabChange(tab.id)}
                              className={`
                                w-full flex items-center px-2.5 py-2 text-sm font-medium rounded-lg transition-all duration-150 group
                                ${isActive
                                  ? `${colors.active} shadow-sm`
                                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-gray-100'
                                }
                              `}
                            >
                              <div className={`
                                flex items-center justify-center w-7 h-7 rounded-md mr-2.5 transition-all duration-150 flex-shrink-0
                                ${isActive ? colors.bg : 'bg-gray-100 dark:bg-gray-700 group-hover:' + colors.bg}
                              `}>
                                <Icon className={`h-4 w-4 ${isActive ? colors.text : 'text-gray-500 dark:text-gray-400 group-hover:' + colors.text}`} />
                              </div>
                              <div className="text-left flex-1 min-w-0">
                                <div className="font-medium truncate leading-tight">{tab.name}</div>
                                <div className={`text-xs truncate leading-tight ${isActive ? 'opacity-60' : 'text-gray-500 dark:text-gray-400'}`}>
                                  {tab.description}
                                </div>
                              </div>
                              {isActive && (
                                <div className={`w-1 h-5 rounded-full flex-shrink-0 ml-1 ${colors.text.replace('text-', 'bg-')}`} />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 w-full md:w-auto">
          <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Rendszer eszközök */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Értesítési eszközök */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <BellIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Értesítési eszközök</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Rendszer értesítések kezelése és tesztelése</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <ClockIcon className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">Határidő ellenőrzés</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Lejáró és lejárt feladatok/projektek ellenőrzése</p>
                        </div>
                      </div>
                      <Button
                        onClick={handleDeadlineCheck}
                        disabled={isRunningDeadlineCheck}
                        className="bg-orange-600 hover:bg-orange-700 text-white"
                      >
                        {isRunningDeadlineCheck ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Futtatás...
                          </>
                        ) : (
                          <>
                            <PlayIcon className="h-4 w-4 mr-2" />
                            Futtatás
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Rendszer statisztikák */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                        <ComputerDesktopIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Rendszer állapot</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Rendszer működés és statisztikák</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Értesítési szolgáltatás</span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                          Aktív
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Deadline ellenőrzés</span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                          Manuális
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Email szolgáltatás</span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                          Aktív
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {activeTab === 'organizations' && (
            <SystemOrganizationManager />
          )}

          {activeTab === 'users' && (
            <SystemUserManager />
          )}

          {activeTab === 'subscriptions' && (
            <SubscriptionManager />
          )}

          {activeTab === 'packages' && (
            <PackageManager />
          )}

          {activeTab === 'modules' && (
            <ModuleStatus />
          )}

          {activeTab === 'billing' && (
            <div className="crm-card">
              <div className="text-center py-12">
                <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                  Számlázás
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Számlák generálása, küldése, nyomon követése
                </p>
              </div>
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="crm-card">
              <div className="text-center py-12">
                <BanknotesIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                  Fizetések
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Stripe/PayPal integráció, tranzakciók, visszatérítések
                </p>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <AnalyticsDashboard />
          )}

          {activeTab === 'system' && (
            <SystemDashboard />
          )}

          {activeTab === 'storage' && (
            <StorageOptimizationDashboard showHeader={false} />
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemAdmin; 